import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { oesApi } from '../services/api';
import Layout from '../components/layout/Layout';
import CustomSelect from '../components/common/CustomSelect';
import ConfirmModal from '../components/ConfirmModal';

const CLASSIFICATION_ORDER = ['Info', 'Low', 'Moderate', 'High', 'Critical'];
const GAUGE_COLORS = {
  Info: '#94a3b8',
  Low: '#38bdf8',
  Moderate: '#facc15',
  High: '#fb923c',
  Critical: '#dc2626',
};

// Values 1–5 used in background; labels are qualitative only
const LIKELIHOOD_OPTIONS = [
  { value: 1, label: 'Very Low' },
  { value: 2, label: 'Low' },
  { value: 3, label: 'Moderate' },
  { value: 4, label: 'High' },
  { value: 5, label: 'Very High' },
];

const TREATMENT_OPTIONS = [
  { value: 'Mitigate', label: 'Mitigate' },
  { value: 'Accept', label: 'Accept' },
  { value: 'Transfer', label: 'Transfer' },
  { value: 'Avoid', label: 'Avoid' },
];

// Arc: 5 equal segments, Info at 225° → Critical at -45°. Needle points to segment center for classification.
const GAUGE_SEGMENT_CENTER_ANGLE = {
  Info: 198,      // center of first segment (225° to 171°)
  Low: 144,       // 171° to 117°
  Moderate: 90,   // 117° to 63°
  High: 36,       // 63° to 9°
  Critical: -18,  // 9° to -45°
};

function RiskGauge({ classification, maxScore }) {
  const cx = 50;
  const cy = 52;
  const r = 42;
  const arcSpan = 270;
  const arcStart = 225;
  const needleAngle = GAUGE_SEGMENT_CENTER_ANGLE[classification] ?? GAUGE_SEGMENT_CENTER_ANGLE.Info;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const arcX = (angle) => cx + r * Math.cos(toRad(angle));
  const arcY = (angle) => cy - r * Math.sin(toRad(angle));
  const segmentAngle = arcSpan / 5;
  const strokeWidth = 12;
  const targetX = arcX(needleAngle);
  const targetY = arcY(needleAngle);
  const dx = targetX - cx;
  const dy = targetY - cy;
  const dist = Math.hypot(dx, dy) || 1;
  const needleLength = 34;
  const needleTipX = cx + (dx / dist) * needleLength;
  const needleTipY = cy + (dy / dist) * needleLength;
  const baseRadius = 3;
  const perpX = -dy / dist;
  const perpY = dx / dist;
  const rightBaseX = cx + perpX * baseRadius;
  const rightBaseY = cy + perpY * baseRadius;
  const leftBaseX = cx - perpX * baseRadius;
  const leftBaseY = cy - perpY * baseRadius;
  const needlePath = `M ${needleTipX} ${needleTipY} L ${rightBaseX} ${rightBaseY} A ${baseRadius} ${baseRadius} 0 0 1 ${leftBaseX} ${leftBaseY} Z`;
  return (
    <div className="flex flex-col items-center">
      <div className="text-sm font-semibold text-slate-600 mb-1">Overall risk</div>
      <div className="relative w-36 h-28">
        <svg viewBox="0 0 100 85" className="w-full h-full">
          {CLASSIFICATION_ORDER.map((level, i) => {
            const a0 = arcStart - i * segmentAngle;
            const a1 = arcStart - (i + 1) * segmentAngle;
            const d = `M ${arcX(a0)} ${arcY(a0)} A ${r} ${r} 0 0 1 ${arcX(a1)} ${arcY(a1)}`;
            return (
              <path
                key={level}
                d={d}
                fill="none"
                stroke={GAUGE_COLORS[level] || '#94a3b8'}
                strokeWidth={strokeWidth}
                strokeLinecap="butt"
              />
            );
          })}
          <path
            d={needlePath}
            fill="#808080"
            stroke="#808080"
            strokeWidth="0.8"
            className="transition-all duration-300 ease-out"
          />
        </svg>
      </div>
      <div className="flex justify-between w-full mt-0.5 px-0.5 text-[10px] text-slate-500">
        <span>Info</span>
        <span>Critical</span>
      </div>
      <div className="mt-1.5 font-semibold text-slate-800" style={{ color: GAUGE_COLORS[classification] || '#64748b' }}>
        {classification}
      </div>
    </div>
  );
}

function getClassificationStyle(classification) {
  const s = (classification || '').toLowerCase();
  if (s === 'critical') return 'bg-red-100 text-red-900';
  if (s === 'high') return 'bg-orange-100 text-orange-900';
  if (s === 'moderate') return 'bg-amber-100 text-amber-900';
  if (s === 'low') return 'bg-sky-100 text-sky-800';
  return 'bg-slate-100 text-slate-700';
}

/** Group matrix rows (one per asset×threat×vuln) by asset for rowspan. */
function groupRowsByAsset(rows) {
  const byAsset = {};
  for (const row of rows) {
    const k = row.asset_name;
    if (!byAsset[k]) byAsset[k] = { asset_name: row.asset_name, valuation: row.valuation, rows: [] };
    byAsset[k].rows.push(row);
  }
  return Object.values(byAsset);
}

function getThreatRowSpan(rows, fromIndex) {
  const tid = rows[fromIndex].threat_id;
  let span = 0;
  for (let i = fromIndex; i < rows.length && rows[i].threat_id === tid; i++) span++;
  return span;
}

/** Group rows by threat_id into blocks { threat_id, threat_name, rows }. */
function groupRowsByThreat(rows) {
  const blocks = [];
  for (let i = 0; i < rows.length; ) {
    const span = getThreatRowSpan(rows, i);
    blocks.push({
      threat_id: rows[i].threat_id,
      threat_name: rows[i].threat_name,
      rows: rows.slice(i, i + span),
    });
    i += span;
  }
  return blocks;
}

function getLabelForValue(value) {
  const v = Number(value);
  return LIKELIHOOD_OPTIONS.find((o) => o.value === v)?.label ?? String(value);
}

/** Map risk score 1–25 to classification (same as backend 5×5). */
function getClassificationFromScore(score) {
  const s = Number(score) || 0;
  if (s >= 15) return 'Critical';
  if (s >= 10) return 'High';
  if (s >= 5) return 'Moderate';
  if (s >= 3) return 'Low';
  return 'Info';
}

const PencilIcon = () => (
  <svg className="w-4 h-4 text-slate-500 hover:text-eucnet-sky cursor-pointer flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4 text-slate-500 hover:text-red-600 cursor-pointer flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ChevronDownIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronRightIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export default function OESRiskMatrix() {
  const { oesId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [oes, setOes] = useState(null);
  const [collapsedAssets, setCollapsedAssets] = useState(() => new Set());
  const [collapsedThreats, setCollapsedThreats] = useState(() => new Set());

  useEffect(() => {
    if (!oesId) return;
    oesApi.getById(Number(oesId))
      .then((r) => setOes(r.data))
      .catch(() => setOes(null));
  }, [oesId]);

  const fetchMatrix = useCallback(async () => {
    try {
      const res = await oesApi.getAssetRiskMatrix(Number(oesId));
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load risk matrix');
    } finally {
      setLoading(false);
    }
  }, [oesId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await oesApi.getAssetRiskMatrix(Number(oesId));
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load risk matrix');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [oesId]);

  const handleVulnDescriptionSave = async (pvId, vulnerability_description) => {
    setSaving(true);
    try {
      await oesApi.patchPotentialVulnerabilityDescription(Number(oesId), pvId, { vulnerability_description: vulnerability_description || null });
      await fetchMatrix();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save vulnerability description');
    } finally {
      setSaving(false);
    }
  };

  const [editingCellKey, setEditingCellKey] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [vulnToDelete, setVulnToDelete] = useState(null);

  const handleCellOverride = async (asset_name, threat_id, potential_vulnerability_id, payload) => {
    if (!data?.assessment_id) return;
    setSaving(true);
    try {
      await oesApi.patchRiskMatrixCell(Number(oesId), {
        assessment_id: data.assessment_id,
        asset_name,
        threat_id,
        potential_vulnerability_id: potential_vulnerability_id ?? undefined,
        ...payload,
      });
      if (payload.treatment_override != null) {
        setData((prev) => {
          if (!prev?.rows) return prev;
          const pvId = potential_vulnerability_id ?? null;
          const newRows = prev.rows.map((row) => {
            if (row.asset_name !== asset_name || row.threat_id !== threat_id) return row;
            const rowPvId = row.vulnerability?.id ?? null;
            if (rowPvId !== pvId) return row;
            return { ...row, treatment: payload.treatment_override };
          });
          return { ...prev, rows: newRows };
        });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  const handleVulnLikelihoodOverride = async (threat_id, potential_vulnerability_id, likelihood_override) => {
    if (!data?.assessment_id) return;
    const value = Number(likelihood_override);
    setData((prev) => {
      if (!prev?.rows) return prev;
      const newRows = prev.rows.map((row) => {
        if (row.threat_id !== threat_id || (row.vulnerability?.id ?? null) !== potential_vulnerability_id) return row;
        return { ...row, risk_x_vulnerability: value };
      });
      return { ...prev, rows: newRows };
    });
    setSaving(true);
    try {
      await oesApi.patchRiskMatrixVulnerabilityCell(Number(oesId), {
        assessment_id: data.assessment_id,
        threat_id,
        potential_vulnerability_id,
        likelihood_override: value,
      });
      await fetchMatrix();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save likelihood');
      await fetchMatrix();
    } finally {
      setSaving(false);
    }
  };

  const handleVulnDeleteClick = (pvId) => {
    setVulnToDelete(pvId);
    setConfirmDeleteOpen(true);
  };

  const handleVulnDeleteConfirm = async () => {
    const pvId = vulnToDelete;
    if (pvId == null) return;
    setConfirmDeleteOpen(false);
    setVulnToDelete(null);
    setSaving(true);
    try {
      await oesApi.deletePotentialVulnerability(Number(oesId), pvId);
      await fetchMatrix();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to delete vulnerability');
    } finally {
      setSaving(false);
    }
  };

  const toggleAssetCollapsed = (assetName) => {
    setCollapsedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(assetName)) next.delete(assetName);
      else next.add(assetName);
      return next;
    });
  };

  const threatKey = (assetName, threatId) => `${assetName}|${threatId}`;

  const toggleThreatCollapsed = (assetName, threatId) => {
    const key = threatKey(assetName, threatId);
    setCollapsedThreats((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12 text-center text-slate-600">Loading risk matrix…</div>
      </Layout>
    );
  }

  if (error && !data) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <Link to="/dashboard" className="text-eucnet-sky hover:underline">Back to dashboard</Link>
        </div>
      </Layout>
    );
  }

  const { assets, rows, overall_max_risk_score, overall_classification } = data || {};
  const assetGroups = rows?.length ? groupRowsByAsset(rows) : [];
  const logoSrc = oes?.logo ? (oes.logo.startsWith('data:') ? oes.logo : `data:image/jpeg;base64,${oes.logo}`) : null;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center mb-4 pb-3 border-b border-slate-200">
          {logoSrc && (
            <img
              src={logoSrc}
              alt=""
              className="w-20 h-20 object-contain mr-3 flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-eucnet-navy">{oes?.name || 'OES'}</h2>
          </div>
        </div>
        <div className="flex flex-wrap items-start justify-between gap-6 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-eucnet-navy">Asset-centric risk matrix</h1>
            <p className="text-slate-600 mt-1 text-sm">
              Overall risk and breakdown by asset, threat, and vulnerability.
            </p>
          </div>
          <div className="flex-shrink-0 bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <RiskGauge classification={overall_classification} maxScore={overall_max_risk_score} />
          </div>
        </div>

        {error && <p className="mb-4 text-red-600">{error}</p>}
        {saving && <p className="mb-2 text-slate-500 text-sm">Saving…</p>}

        {!rows?.length ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-600 text-sm">
            No assets or threat risks yet. Complete the criticality assessment (with IT/OT systems) and the posture questionnaire to derive the matrix.
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-sm">
                    <th className="py-2 px-3 font-semibold text-slate-700 w-28">Asset</th>
                    <th className="py-2 px-3 font-semibold text-slate-700 w-28">Threat</th>
                    <th className="py-2 px-3 font-semibold text-slate-700 min-w-[200px]">Vulnerability</th>
                    <th className="py-2 px-3 font-semibold text-slate-700 w-24">Likelihood</th>
                    <th className="py-2 px-3 font-semibold text-slate-700 w-24">Risk</th>
                    <th className="py-2 px-3 font-semibold text-slate-700 w-26">Treatment</th>
                  </tr>
                </thead>
                <tbody>
                  {assetGroups.flatMap((group) => {
                    const assetName = group.asset_name;
                    const isAssetCollapsed = collapsedAssets.has(assetName);
                    if (isAssetCollapsed) {
                      const threatCount = groupRowsByThreat(group.rows).length;
                      return [
                        <tr key={`asset-${assetName}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                          <td colSpan={6} className="py-2 px-3 align-middle border-r border-slate-100">
                            <button
                              type="button"
                              onClick={() => toggleAssetCollapsed(assetName)}
                              className="flex items-center gap-2 w-full text-left font-medium text-eucnet-navy hover:text-eucnet-sky"
                              aria-expanded="false"
                            >
                              <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                              {assetName}
                              <span className="text-slate-500 font-normal text-xs">
                                — {threatCount} threat{threatCount !== 1 ? 's' : ''}
                              </span>
                            </button>
                          </td>
                        </tr>,
                      ];
                    }
                    const threatBlocks = groupRowsByThreat(group.rows);
                    const assetVisibleRowCount = threatBlocks.reduce(
                      (acc, b) => acc + (collapsedThreats.has(threatKey(assetName, b.threat_id)) ? 1 : b.rows.length),
                      0
                    );
                    let assetRowIndex = 0;
                    return threatBlocks.flatMap((block) => {
                      const tKey = threatKey(assetName, block.threat_id);
                      const isThreatCollapsed = collapsedThreats.has(tKey);
                      const showAssetCell = assetRowIndex === 0;
                      if (isThreatCollapsed) {
                        assetRowIndex += 1;
                        return [
                          <tr key={`threat-${tKey}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                            {showAssetCell && (
                              <td rowSpan={assetVisibleRowCount} className="py-2 px-3 font-medium text-eucnet-navy align-top border-r border-slate-100">
                                <button
                                  type="button"
                                  onClick={() => toggleAssetCollapsed(assetName)}
                                  className="flex items-center gap-1.5 text-left hover:text-eucnet-sky"
                                  aria-expanded="true"
                                >
                                  <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                                  {assetName}
                                </button>
                              </td>
                            )}
                            <td className="py-2 px-3 text-slate-700 align-top border-r border-slate-100">
                              <button
                                type="button"
                                onClick={() => toggleThreatCollapsed(assetName, block.threat_id)}
                                className="flex items-center gap-1.5 text-left hover:text-eucnet-sky"
                                aria-expanded="false"
                              >
                                <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                                {block.threat_name}
                              </button>
                            </td>
                            <td colSpan={4} className="py-2 px-3 text-slate-500 text-xs">
                              {block.rows.length} vulnerabilit{block.rows.length !== 1 ? 'ies' : 'y'}
                            </td>
                          </tr>,
                        ];
                      }
                      assetRowIndex += block.rows.length;
                      return block.rows.map((row, i) => {
                        const vuln = row.vulnerability;
                        const vulnDisplayText = vuln
                          ? (vuln.vulnerability_description && vuln.vulnerability_description.trim())
                            ? vuln.vulnerability_description.trim()
                            : [vuln.vulnerability_title, vuln.vulnerability_description].filter(Boolean).join(' — ') || ''
                          : '';
                        const cellKey = vuln ? `${row.asset_name}-${row.threat_id}-${vuln.id}` : null;
                        const isEditing = cellKey != null && editingCellKey === cellKey;
                        const isFirstInThreat = i === 0;
                        return (
                          <tr key={`${row.asset_name}-${row.threat_id}-${vuln?.id ?? i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                            {showAssetCell && i === 0 && (
                              <td rowSpan={assetVisibleRowCount} className="py-2 px-3 font-medium text-eucnet-navy align-top border-r border-slate-100">
                                <button
                                  type="button"
                                  onClick={() => toggleAssetCollapsed(assetName)}
                                  className="flex items-center gap-1.5 text-left hover:text-eucnet-sky"
                                  aria-expanded="true"
                                >
                                  <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                                  {assetName}
                                </button>
                              </td>
                            )}
                            {isFirstInThreat && (
                              <td rowSpan={block.rows.length} className="py-2 px-3 text-slate-700 align-top border-r border-slate-100">
                                <button
                                  type="button"
                                  onClick={() => toggleThreatCollapsed(assetName, block.threat_id)}
                                  className="flex items-center gap-1.5 text-left hover:text-eucnet-sky"
                                  aria-expanded="true"
                                >
                                  <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                                  {block.threat_name}
                                </button>
                              </td>
                            )}
                            <td className="py-1.5 px-3 align-top">
                              {vuln ? (
                                <div className="flex items-start gap-1">
                                  {isEditing ? (
                                    <textarea
                                      key={cellKey}
                                      className="flex-1 rounded border border-slate-200 px-2 py-1 text-slate-800 text-xs min-h-[52px]"
                                      defaultValue={vulnDisplayText}
                                      onBlur={(e) => {
                                        const val = e.target.value.trim();
                                        handleVulnDescriptionSave(vuln.id, val || null);
                                        setEditingCellKey(null);
                                      }}
                                      placeholder="Title — description"
                                      autoFocus
                                    />
                                  ) : (
                                    <span className="flex-1 text-xs text-slate-800">{vulnDisplayText || '—'}</span>
                                  )}
                                  {!isEditing && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setEditingCellKey(cellKey);
                                        }}
                                        className="p-0.5 rounded hover:bg-slate-100"
                                        aria-label="Edit"
                                      >
                                        <PencilIcon />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleVulnDeleteClick(vuln.id);
                                        }}
                                        className="p-0.5 rounded hover:bg-red-50"
                                        aria-label="Delete"
                                      >
                                        <TrashIcon />
                                      </button>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-400">None</span>
                              )}
                            </td>
                            <td className="py-1.5 px-3 align-middle whitespace-nowrap">
                              {vuln ? (
                                <CustomSelect
                                  value={row.risk_x_vulnerability}
                                  onChange={(e) => handleVulnLikelihoodOverride(row.threat_id, vuln.id, e.target.value)}
                                  options={LIKELIHOOD_OPTIONS}
                                  placeholder="Likelihood"
                                  searchable={false}
                                  size="sm"
                                  className="min-w-[7rem] max-w-[7rem] shrink-0"
                                />
                              ) : (
                                <span className="text-slate-600">{getLabelForValue(row.risk_x_vulnerability)}</span>
                              )}
                            </td>
                            <td className="py-2 px-3 align-top">
                              <span className={`inline-block px-1.5 py-0.5 rounded font-medium ${getClassificationStyle(getClassificationFromScore((Number(row.risk_x_vulnerability) || 0) * (Number(row.valuation) || 0)))}`}>
                                {getClassificationFromScore((Number(row.risk_x_vulnerability) || 0) * (Number(row.valuation) || 0))}
                              </span>
                            </td>
                            <td className="py-1.5 px-3 align-top">
                              <CustomSelect
                                value={row.treatment}
                                onChange={(e) => handleCellOverride(row.asset_name, row.threat_id, vuln?.id ?? null, { treatment_override: e.target.value })}
                                options={TREATMENT_OPTIONS}
                                placeholder="Treatment"
                                searchable={false}
                                size="sm"
                                className="min-w-[6rem]"
                              />
                            </td>
                          </tr>
                        );
                      });
                    });
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="mt-6 flex gap-3">
          <Link to={`/oes/${oesId}/questionnaire-result`} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            Posture result
          </Link>
          <Link to="/dashboard" className="px-4 py-2 rounded-lg bg-eucnet-sky text-white hover:bg-eucnet-navy">
            Back to dashboard
          </Link>
        </div>

        <ConfirmModal
          open={confirmDeleteOpen}
          title="Remove vulnerability?"
          message="Remove this vulnerability from the risk matrix? It will no longer appear in the assessment."
          confirmText="Remove"
          cancelText="Cancel"
          onConfirm={handleVulnDeleteConfirm}
          onCancel={() => {
            setConfirmDeleteOpen(false);
            setVulnToDelete(null);
          }}
        />
      </div>
    </Layout>
  );
}
